import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, ViewEncapsulation, NgZone, signal, computed, inject } from '@angular/core';
import { LangChangeEvent, TranslateService, TranslatePipe } from '@ngx-translate/core';
import { StorageService } from '../../services/storage.service';
import { UserContextService } from '../../services/user-context.service';
import { TranslationService } from '../../services/translation.service';
import { ThemeService } from '../../services/theme.service';
import { AnalyticsService } from '../../services/analytics.service';
import { Member } from '../../model/member';
import { MemberStateService } from '../../services/member-state.service';
import { AuthService } from '../../auth/services/auth.service';
import { PlatformService } from '../../auth/services/platform.service';
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { TitleCasePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { APP_CONFIG } from '../../config/app-config.token';
import { AppConfig } from '../../config/app-config.model';
import { LayoutService } from '../../navigation/layout.service';

@Component({
    selector: 'myabvv-menu',
    standalone: true,
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [TranslatePipe, RouterLink, RouterLinkActive, TitleCasePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, OnDestroy {
  private static readonly LOGOS: Record<string, string> = {
    nl: './assets/img/ABVV.svg',
    fr: './assets/img/FGTB.svg'
  };

  // Secret 5-tap on logo activates test mode (mobile only, for app store reviewers)
  private static readonly TEST_MODE_TAPS = 5;
  private static readonly TEST_MODE_TIMEOUT_MS = 2000;

  private translateService = inject(TranslateService);
  private translationService = inject(TranslationService);
  private storage = inject(StorageService);
  private memberStateService = inject(MemberStateService);
  private userContext = inject(UserContextService);
  private authService = inject(AuthService);
  private platformService = inject(PlatformService);
  protected themeService = inject(ThemeService);
  private analyticsService = inject(AnalyticsService);
  private ngZone = inject(NgZone);
  private router = inject(Router);
  layout = inject(LayoutService);
  private config = inject<AppConfig>(APP_CONFIG);

  member = signal<Member | null>(null);
  givenName = signal<string | null>(null);
  userIdentifier = signal<string | null>(null);
  isInternalUser = signal(false);
  isAuthenticated = signal(false);
  isMobile = signal(false);
  currentLang = signal('nl');
  logoUrl = computed(() => MenuComponent.LOGOS[this.currentLang()] ?? MenuComponent.LOGOS['nl']);
  mobileHeaderName = computed(() => this.isInternalUser() ? this.userIdentifier() : this.givenName());
  // Member: "First Last (INSS)". Employee: "employee id (INSS of the consulted member)".
  headerUserLabel = computed(() => {
    const info = this.member()?.personalInformation;
    const main = this.isInternalUser()
      ? this.userIdentifier()
      : [info?.firstName, info?.lastName].filter(Boolean).join(' ') || null;
    const detail = this.isInternalUser() ? info?.inss : this.userIdentifier();
    return main && detail ? `${main} (${detail})` : main || detail || '';
  });
  private subscriptions: Subscription[] = [];
  private tapCount = 0;
  private lastTapTime = 0;

  ngOnInit() {
    // Platform detection
    this.isMobile.set(this.platformService.isMobile());

    // Initial language (logoUrl is computed from currentLang automatically)
    this.currentLang.set(this.translateService.currentLang || 'nl');

    // Initial values - try to get them immediately
    this.updateUserInfo();

    // Also update after a short delay to catch late-loading profiles
    setTimeout(() => this.updateUserInfo(), 100);

    // Subscribe to member changes
    this.subscriptions.push(
      this.memberStateService.getMember().subscribe({
        next: member => {
          this.member.set(member);
        },
        error: err => console.error('[MenuComponent] Error loading member:', err)
      })
    );

    // Subscribe to language changes
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe({
        next: (_e: LangChangeEvent) => {
          this.currentLang.set(_e.lang);
        },
        error: (err: unknown) => console.error('[MenuComponent] Error on language change:', err)
      })
    );

    // Subscribe to user profile changes (for modern auth)
    this.subscriptions.push(
      this.authService.userProfile$.subscribe({
        next: _profile => {
          this.ngZone.run(() => this.updateUserInfo());
        },
        error: err => console.error('[MenuComponent] Error on user profile change:', err)
      })
    );

    // Refresh user info on navigation and close drawer if open
    this.subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe({
          next: () => {
            this.updateUserInfo();
          },
          error: err => console.error('[MenuComponent] Error on navigation event:', err)
        })
    );
  }

  private updateUserInfo(): void {
    try {
      const newGivenName = this.userContext.getGivenName();
      const newUserIdentifier = this.userContext.getUserIdentifier();
      const newIsInternalUser = this.userContext.isInternalUser();
      const newIsAuthenticated = this.authService.isAuthenticated();

      // Only update if values changed (signals handle change detection automatically)
      if (this.givenName() !== newGivenName ||
          this.userIdentifier() !== newUserIdentifier ||
          this.isInternalUser() !== newIsInternalUser ||
          this.isAuthenticated() !== newIsAuthenticated) {
        this.givenName.set(newGivenName);
        this.userIdentifier.set(newUserIdentifier);
        this.isInternalUser.set(newIsInternalUser);
        this.isAuthenticated.set(newIsAuthenticated);
      }
    } catch (error) {
      console.error('[MenuComponent] Error updating user info:', error);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  changeLang() {
    this.translationService.toggleLanguage();
  }

  onLogoTap() {
    if (!this.isMobile()) return;

    const now = Date.now();
    const timedOut = now - this.lastTapTime > MenuComponent.TEST_MODE_TIMEOUT_MS;
    this.tapCount = timedOut ? 1 : this.tapCount + 1;
    this.lastTapTime = now;

    if (this.tapCount >= MenuComponent.TEST_MODE_TAPS) {
      this.tapCount = 0;
      this.router.navigateByUrl('/auth/landing');
    }
  }

  toggleDarkMode() {
    this.themeService.toggleDarkMode();
  }

  translate(lang: string) {
    this.translationService.changeLanguage(lang);
  }

  logout() {
    // Logic (analytics + storage cleanup + auth logout) is centralized in UserContextService
    // so the header and sidebar logout buttons behave identically.
    this.userContext.logout();
  }
}
